import React from 'react';

import { Box, Center, Flex, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { DroxyChatPreviewContainer } from 'components/Droxy/DroxyChatPreviewContainer';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SectionNumber } from 'components/SectionNumber';
import { useUserContext } from 'contexts/UserContext';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { t } from 'i18next';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES,
  DEFAULT_LEAD_COLLECTION_TOOL_CONFIG_FORM_VALUES
} from 'shared/constants/smart-assistant';
import { ToolEventType } from 'shared/interfaces/ChatMessage';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { InfoType } from 'shared/interfaces/tools/CollectLeadsInfo';
import { EmailHandoffToolConfig } from 'shared/interfaces/tools/EmailHandoff';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { formHasFieldErrors } from 'shared/utils/form';
import {
  emailValidator,
  requiredInputValidator,
  validateEmailHandoffInfoToCollect,
  validateNotEmptyArray
} from 'shared/utils/form-validators';

enum EmailHandoffPanelSections {
  GENERAL_SETTINGS = 'general-settings',
  INFORMATION_TO_COLLECT = 'information-to-collect'
}

interface EmailHandoffToolConfigurationPanelProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
  smartAssistant: SmartAssistant;
}

export function EmailHandoffToolConfigurationPanel({
  onUpdate,
  onBack,
  smartAssistant
}: EmailHandoffToolConfigurationPanelProps) {
  const [user] = useUserContext();
  const location = useLocation();
  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({ smartAssistant });
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingToolConfig = toolConfigs.find(
    (config) => config.kind === ToolKind.EMAIL_HANDOFF
  ) as EmailHandoffToolConfig;
  const isNewToolConfig = !maybeExistingToolConfig;

  const emailHandoffToolConfigForm = useForm<EmailHandoffToolConfig>({
    initialValues: {
      _id: maybeExistingToolConfig?._id,
      kind: ToolKind.EMAIL_HANDOFF as const,
      toolName: ToolKind.EMAIL_HANDOFF as const,
      trigger: ToolTrigger.INTENT as const,
      enabled: maybeExistingToolConfig?.enabled ?? true,
      infoTypesToCollect:
        maybeExistingToolConfig?.infoTypesToCollect || DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES.infoTypesToCollect,
      labels: maybeExistingToolConfig?.labels || {
        email: DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES.emailLabel,
        name: DEFAULT_LEAD_COLLECTION_TOOL_CONFIG_FORM_VALUES.nameLabel,
        phone: DEFAULT_LEAD_COLLECTION_TOOL_CONFIG_FORM_VALUES.phoneLabel,
        reason: DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES.reasonLabel
      },
      title: maybeExistingToolConfig?.title || DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES.title,
      emailToHandoff: maybeExistingToolConfig?.emailToHandoff || user.email || ''
    },
    validate: {
      infoTypesToCollect: (value) => validateNotEmptyArray(value) || validateEmailHandoffInfoToCollect(value),
      labels: {
        email: (value) => requiredInputValidator(value || ''),
        name: (value, allValues) =>
          allValues.infoTypesToCollect.includes(InfoType.NAME) && requiredInputValidator(value || ''),
        phone: (value, allValues) =>
          allValues.infoTypesToCollect.includes(InfoType.PHONE) && requiredInputValidator(value || ''),
        reason: (value, allValues) =>
          allValues.infoTypesToCollect.includes(InfoType.REASON) && requiredInputValidator(value || '')
      },
      title: (value) => requiredInputValidator(value),
      emailToHandoff: (value) => requiredInputValidator(value) || emailValidator(value)
    }
  });

  const handleInfoTypeToggle = (infoType: InfoType, checked: boolean) => {
    const currentValues = emailHandoffToolConfigForm.values.infoTypesToCollect;

    // Email is always required, so don't allow unchecking
    if (infoType === InfoType.EMAIL) return;

    const newValues = checked ? [...currentValues, infoType] : currentValues.filter((type) => type !== infoType);
    emailHandoffToolConfigForm.setFieldValue('infoTypesToCollect', newValues);
  };

  const handleUpdate = () => {
    const validation = emailHandoffToolConfigForm.validate();
    if (validation.hasErrors) return;
    onUpdate([emailHandoffToolConfigForm.values]);
  };

  const previewMessages = [
    {
      text: '',
      toolEvent: { type: ToolEventType.EMAIL_HANDOFF },
      fromUser: false
    }
  ];
  const previewToolConfigs = [emailHandoffToolConfigForm.values];

  const infoTypesToCollect = emailHandoffToolConfigForm.values.infoTypesToCollect;

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => emailHandoffToolConfigForm.reset()}
      hasChanges={emailHandoffToolConfigForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={emailHandoffToolConfigForm.values.enabled}
          onToggle={(enabled) => emailHandoffToolConfigForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Flex direction="row" gap="xl" align="flex-start" w="100%">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <DroxyAccordion defaultOpenSections={[EmailHandoffPanelSections.GENERAL_SETTINGS]}>
            <DroxyAccordionSection
              title={t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.SECTIONS.GENERAL_SETTINGS.TITLE')}
              section={EmailHandoffPanelSections.GENERAL_SETTINGS}
              icon={<SectionNumber number={1} />}
              hasError={formHasFieldErrors(emailHandoffToolConfigForm, 'title', 'emailToHandoff')}>
              <Stack spacing="md">
                <TextInput
                  label={
                    <InputLabelWithInfo
                      label={t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.FORM_TITLE.LABEL')}
                      infoContentTranslationKey="SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.FORM_TITLE.INFO"
                    />
                  }
                  maxLength={200}
                  {...emailHandoffToolConfigForm.getInputProps('title')}
                />
                <TextInput
                  label={
                    <InputLabelWithInfo
                      label={t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.EMAIL_TO_HANDOFF.LABEL')}
                      infoContentTranslationKey="SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.EMAIL_TO_HANDOFF.INFO"
                    />
                  }
                  maxLength={100}
                  {...emailHandoffToolConfigForm.getInputProps('emailToHandoff')}
                />
              </Stack>
            </DroxyAccordionSection>

            <DroxyAccordionSection
              title={t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.SECTIONS.INFO_TO_COLLECT.TITLE')}
              section={EmailHandoffPanelSections.INFORMATION_TO_COLLECT}
              icon={<SectionNumber number={2} />}
              hasError={formHasFieldErrors(emailHandoffToolConfigForm, 'infoTypesToCollect', 'labels')}>
              <Stack spacing="lg">
                <Stack spacing="sm">
                  <Flex direction="row" align="center" justify="space-between">
                    <Text fw={500}>
                      Email
                      <Text span c="red" component="span" size="xs" ml={2} sx={{ verticalAlign: 'super' }}>
                        *
                      </Text>
                    </Text>
                    <Switch
                      checked={infoTypesToCollect.includes(InfoType.EMAIL)}
                      onChange={(event) => handleInfoTypeToggle(InfoType.EMAIL, event.currentTarget.checked)}
                      disabled={true}
                    />
                  </Flex>
                  <TextInput
                    placeholder="Email Address"
                    {...emailHandoffToolConfigForm.getInputProps('labels.email')}
                  />
                </Stack>

                <Stack spacing="sm">
                  <Flex direction="row" align="center" justify="space-between">
                    <Text fw={500}>Name</Text>
                    <Switch
                      checked={infoTypesToCollect.includes(InfoType.NAME)}
                      onChange={(event) => handleInfoTypeToggle(InfoType.NAME, event.currentTarget.checked)}
                    />
                  </Flex>
                  {infoTypesToCollect.includes(InfoType.NAME) && (
                    <TextInput placeholder="Full Name" {...emailHandoffToolConfigForm.getInputProps('labels.name')} />
                  )}
                </Stack>

                <Stack spacing="sm">
                  <Flex direction="row" align="center" justify="space-between">
                    <Text fw={500}>Phone</Text>
                    <Switch
                      checked={infoTypesToCollect.includes(InfoType.PHONE)}
                      onChange={(event) => handleInfoTypeToggle(InfoType.PHONE, event.currentTarget.checked)}
                    />
                  </Flex>
                  {infoTypesToCollect.includes(InfoType.PHONE) && (
                    <TextInput
                      placeholder="Phone Number"
                      {...emailHandoffToolConfigForm.getInputProps('labels.phone')}
                    />
                  )}
                </Stack>

                <Stack spacing="sm">
                  <Flex direction="row" align="center" justify="space-between">
                    <Text fw={500}>Reason</Text>
                    <Switch
                      checked={infoTypesToCollect.includes(InfoType.REASON)}
                      onChange={(event) => handleInfoTypeToggle(InfoType.REASON, event.currentTarget.checked)}
                    />
                  </Flex>
                  {infoTypesToCollect.includes(InfoType.REASON) && (
                    <TextInput
                      placeholder="Reason for Contact"
                      {...emailHandoffToolConfigForm.getInputProps('labels.reason')}
                    />
                  )}
                </Stack>
              </Stack>
            </DroxyAccordionSection>
          </DroxyAccordion>
        </Box>
        <Center w="35%">
          <DroxyChatPreviewContainer
            messages={previewMessages}
            toolConfigs={previewToolConfigs}
            smartAssistantVisualConfig={smartAssistantVisualConfig}
            visibilityOverrides={{
              [ToolKind.EMAIL_HANDOFF]: true
            }}
            height={650}
          />
        </Center>
      </Flex>
    </ToolConfigurationPanelWrapper>
  );
}
